import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildLoginRedirectUrl } from '../services/appUrls';
import { trackLoginStart } from '../services/analytics';

const Pricing = () => {
  const { t } = useTranslation();
  const loginUrl = buildLoginRedirectUrl();
  const plans = [
    {
      id: 'free',
      name: t('landing.pricing.plans.free.name'),
      description: t('landing.pricing.plans.free.description'),
      price: '$0',
      features: [
        t('landing.pricing.features.threeAliases'),
        t('landing.pricing.features.emailsPerMonth', { count: 10 }),
        t('landing.pricing.features.basicSpam'),
        t('landing.pricing.features.aliasDeactivation')
      ],
      action: t('landing.pricing.buttons.signUpFree'),
      cardClass: 'border-gray-200 bg-white shadow-lg',
      buttonClass: 'bg-slate-900 text-white hover:bg-slate-800'
    },
    {
      id: 'plus',
      name: t('landing.pricing.plans.plus.name'),
      description: t('landing.pricing.plans.plus.description'),
      price: '$3.9',
      features: [
        t('landing.pricing.features.unlimitedAliases'),
        t('landing.pricing.features.emailsPerMonth', { count: 100 }),
        t('landing.pricing.features.replyFromAlias'),
        t('landing.pricing.features.sendFromAlias')
      ],
      action: t('landing.pricing.unlockPlus'),
      cardClass: 'border-blue-500 bg-white shadow-xl',
      buttonClass: 'bg-blue-600 text-white hover:bg-blue-700',
      popular: true
    },
    {
      id: 'pro',
      name: t('landing.pricing.plans.pro.name'),
      description: t('landing.pricing.plans.pro.description'),
      price: '$9.99',
      features: [
        t('landing.pricing.features.unlimitedAliases'),
        t('landing.pricing.features.emailsPerMonth', { count: 1000 }),
        t('landing.pricing.features.replyFromAlias'),
        t('landing.pricing.features.sendFromAlias'),
        t('landing.pricing.features.analytics')
      ],
      action: t('landing.pricing.buttons.choosePro'),
      cardClass: 'border-slate-300 bg-white shadow-lg',
      buttonClass: 'bg-slate-900 text-white hover:bg-slate-800'
    }
  ];

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('landing.pricing.upgradeTitle')}
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t('landing.pricing.upgradeSubtitle')}
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3 md:items-stretch">
          {plans.map((plan) => (
            <article key={plan.id} className={`relative flex flex-col rounded-xl border-2 ${plan.cardClass}`}>
              {plan.popular && (
                <div className="rounded-t-lg bg-blue-500 px-4 py-2 text-center font-medium text-white">
                  {t('landing.pricing.mostPopular')}
                </div>
              )}
              <div className="flex flex-1 flex-col p-6">
                <h3 className="mb-2 text-2xl font-bold text-gray-900">{plan.name}</h3>
                <p className="mb-6 text-gray-700">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600">{t('landing.pricing.perMonth')}</span>
                </div>
                <ul className="mb-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mb-6 mt-auto rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{t('landing.pricing.privacyTitle')}</p>
                  <p>{t('landing.pricing.privacySubtitle')}</p>
                </div>
                <a
                  href={loginUrl}
                  onClick={trackLoginStart}
                  className={`block w-full rounded-md px-4 py-3 text-center font-semibold transition-colors ${plan.buttonClass}`}
                >
                  {plan.action}
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
